import { useState } from 'react';
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Group, PlayArrow, Settings } from '@mui/icons-material';
import {
  GeneralTableBox,
  HeadTableCell,
  LoadingSkeleton,
  NoDataSkeleton,
  RowTableCell,
} from '../../../common/components';
import {
  useNotificationConfigs,
  useUpdateNotificationConfig,
} from '../../../hooks/useNotifications';
import { CHANNEL_LABELS, COUPON_DUE_TYPE } from '../constants/notificationsConstants';
import { formatDateTime, statusChip } from '../utils/notificationUtils';
import {
  getScheduleSummary,
  NotificationScheduleDialog,
} from './NotificationScheduleDialog';
import { NotificationUsersDialog, RECIPIENT_STRATEGY_LABELS } from './NotificationUsersDialog';
import { NotificationsOperationsDialog } from './NotificationsOperationsDialog';

export const RulesTable = () => {
  const { data, isLoading } = useNotificationConfigs({ target_app_module: 'habitat_99' });
  const updateConfig = useUpdateNotificationConfig();
  const [usersConfig, setUsersConfig] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [operationsRuleUuid, setOperationsRuleUuid] = useState('');
  const configs = data?.results || [];

  return (
    <>
      <Paper elevation={0}>
        <GeneralTableBox>
          <Table>
            <TableHead>
              <TableRow>
                <HeadTableCell>Regla</HeadTableCell>
                <HeadTableCell>Canales</HeadTableCell>
                <HeadTableCell>Urgencia</HeadTableCell>
                <HeadTableCell>Estado</HeadTableCell>
                <HeadTableCell>Programación</HeadTableCell>
                <HeadTableCell>Última ejecución</HeadTableCell>
                <HeadTableCell>Acciones</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LoadingSkeleton />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && configs.length === 0 && (
                <NoDataSkeleton colSpan={7} message="No hay configuraciones cargadas." />
              )}
              {configs.map((configItem) => {
                const isMobileCouponDueRule =
                  (configItem.rule_key || configItem.config_key) === COUPON_DUE_TYPE;

                return (
                  <TableRow key={configItem.uuid} hover>
                    <RowTableCell sx={{ minWidth: 320 }}>
                      <Tooltip
                        arrow
                        placement="right"
                        title={
                          <Stack spacing={0.75}>
                            <Typography variant="subtitle2">{configItem.display_name}</Typography>
                            <Typography variant="body2">{configItem.description || '-'}</Typography>
                            <Typography variant="caption">
                              Estrategia:{' '}
                              {isMobileCouponDueRule
                                ? 'Suscriptores mobile según vencimiento'
                                : RECIPIENT_STRATEGY_LABELS[configItem.recipient_strategy] ||
                                  configItem.recipient_strategy}
                            </Typography>
                            <Typography variant="caption">
                              Destinatarios:{' '}
                              {isMobileCouponDueRule
                                ? 'Suscriptores de la app mobile con cupón próximo a vencer'
                                : `${configItem.users?.length || 0} usuario(s)`}
                            </Typography>
                            <Typography variant="caption">
                              Intervalo: {configItem.execution_interval_minutes || '-'} min
                            </Typography>
                            <Typography variant="caption">
                              Programación: {getScheduleSummary(configItem)}
                            </Typography>
                          </Stack>
                        }
                      >
                        <Typography variant="subtitle2" sx={{ cursor: 'help' }}>
                          {configItem.display_name}
                        </Typography>
                      </Tooltip>
                    </RowTableCell>
                    <RowTableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(configItem.channels || []).map((channel) => (
                          <Chip
                            key={channel}
                            label={CHANNEL_LABELS[channel] || channel}
                            size="small"
                          />
                        ))}
                      </Stack>
                    </RowTableCell>
                    <RowTableCell>
                      <Switch
                        checked={Boolean(configItem.is_urgent)}
                        onChange={(event) =>
                          updateConfig.mutate({
                            uuid: configItem.uuid,
                            payload: { is_urgent: event.target.checked },
                          })
                        }
                      />
                    </RowTableCell>
                    <RowTableCell>
                      <Switch
                        checked={Boolean(configItem.is_enabled)}
                        onChange={(event) =>
                          updateConfig.mutate({
                            uuid: configItem.uuid,
                            payload: { is_enabled: event.target.checked },
                          })
                        }
                      />
                    </RowTableCell>
                    <RowTableCell>
                      <Typography variant="body2">
                        {getScheduleSummary(configItem)}
                      </Typography>
                    </RowTableCell>
                    <RowTableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          {formatDateTime(configItem.last_run_at)}
                        </Typography>
                        {configItem.last_run_status && statusChip(configItem.last_run_status)}
                      </Stack>
                    </RowTableCell>
                    <RowTableCell>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {!isMobileCouponDueRule && (
                          <Tooltip title={`Usuarios destinatarios (${configItem.users?.length || 0})`}>
                            <IconButton size="small" onClick={() => setUsersConfig(configItem)}>
                              <Group fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Configurar programación">
                          <IconButton size="small" onClick={() => setScheduleConfig(configItem)}>
                            <Settings fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Abrir operaciones">
                          <IconButton
                            size="small"
                            onClick={() => setOperationsRuleUuid(configItem.uuid_rule)}
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </RowTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </GeneralTableBox>
      </Paper>
      <NotificationUsersDialog
        open={Boolean(usersConfig)}
        configItem={
          configs.find((configItem) => configItem.uuid === usersConfig?.uuid) || usersConfig
        }
        onClose={() => setUsersConfig(null)}
      />
      <NotificationScheduleDialog
        open={Boolean(scheduleConfig)}
        configItem={
          configs.find((configItem) => configItem.uuid === scheduleConfig?.uuid) || scheduleConfig
        }
        onClose={() => setScheduleConfig(null)}
      />
      <NotificationsOperationsDialog
        open={Boolean(operationsRuleUuid)}
        initialRuleUuid={operationsRuleUuid}
        onClose={() => setOperationsRuleUuid('')}
      />
    </>
  );
};
